/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import Drawer from '@mui/material/Drawer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import { format } from 'date-fns';
import { Box, Divider, Typography } from '@mui/material';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import ReactApexChart from 'react-apexcharts';
import ApexCharts from 'apexcharts';

const CLIENT_BITRATE_OPTIONS = {
  chart: {
    id: 'bitrateGraph',
    height: 250,
    type: 'area',
    animations: {
      enabled: true,
      easing: 'linear',
      dynamicAnimation: {
        speed: 500,
      },
    },
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2.5,
  },
  title: {
    text: 'Client bitrate',
    align: 'left',
  },
  markers: {
    size: 0,
  },
  xaxis: {
    type: 'numeric',

    labels: {
      formatter(value) {
        return format(new Date(value), 'HH:mm:ss');
      },
    },
  },
  yaxis: {
    forceNiceScale: true,
    title: {
      text: 'bitrate',
    },
    min: 0,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    floating: true,
    offsetY: -25,
    offsetX: -5,
  },
};

export const FIR_PLI_NACK_OPTIONS = {
  chart: {
    id: 'firPliNackInfo',
    height: 250,
    type: 'line',
    animations: {
      enabled: true,
      easing: 'linear',
      dynamicAnimation: {
        speed: 500,
      },
    },
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2.5,
  },
  title: {
    text: 'firCount pliCount and nackCount',
    align: 'left',
  },
  markers: {
    size: 0,
  },
  xaxis: {
    type: 'numeric',

    labels: {
      formatter(value) {
        return format(new Date(value), 'HH:mm:ss');
      },
    },
  },
  yaxis: {
    forceNiceScale: true,
    title: {
      text: 'count',
    },
    min: 0,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    floating: true,
    offsetY: -18,
  },
};

export const FRAME_SIZE_OPTIONS = {
  chart: {
    id: 'frameSizeInfo',
    height: 250,
    type: 'line',
    animations: {
      enabled: true,
      easing: 'linear',
      dynamicAnimation: {
        speed: 500,
      },
    },
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'stepline',
    width: 2.5,
  },
  title: {
    text: 'Frame size',
    align: 'left',
  },
  markers: {
    size: 0,
  },
  xaxis: {
    type: 'numeric',

    labels: {
      formatter(value) {
        return format(new Date(value), 'HH:mm:ss');
      },
    },
  },
  yaxis: {
    forceNiceScale: true,
    title: {
      text: 'height',
    },
    min: 0,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    floating: true,
    offsetY: -25,
    offsetX: -5,
  },
};

export const FPS_OPTIONS = {
  chart: {
    id: 'fpsGraphInfo',
    height: 250,
    type: 'line',
    animations: {
      enabled: true,
      easing: 'linear',
      dynamicAnimation: {
        speed: 500,
      },
    },
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: 'smooth',
    width: 2.5,
  },
  title: {
    text: 'Client fps',
    align: 'left',
  },
  markers: {
    size: 0,
  },
  xaxis: {
    type: 'numeric',

    labels: {
      formatter(value) {
        return format(new Date(value), 'HH:mm:ss');
      },
    },
  },
  yaxis: {
    forceNiceScale: true,
    title: {
      text: 'FPS',
    },
    min: 0,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'right',
    floating: true,
    offsetY: -25,
    offsetX: -5,
  },
};

export default function SensorInfoDrawer({ open, onClose, info, peerConnection }) {
  const infoWebRtcStatsInterval = useRef();
  const prevDate = useRef();
  const prevBytesReceived = useRef();
  const clientBitrateGraphData = useRef([]);
  const firPliNackGraphData = useRef([]);
  const frameSizeGraphData = useRef([]);
  const fpsGraphData = useRef([]);
  useEffect(() => {
    const fetchData = () => {
      if (peerConnection.current) {
        const currDate = new Date().getTime();
        peerConnection.current.getStats(null).then((stats) => {
          stats.forEach((report) => {
            if (report.type === 'inbound-rtp' && report.kind === 'video') {
              if (Object.prototype.hasOwnProperty.call(report, 'bytesReceived')) {
                const currBytesReceived = Number(report.bytesReceived) / 1000;
                if (prevDate.current) {
                  const clientBitrate = Number(
                    (
                      (8 * (currBytesReceived - prevBytesReceived.current)) /
                      (currDate / 1000 - prevDate.current / 1000)
                    ).toFixed(4)
                  );
                  {
                    let f = 0;
                    for (let i = 0; i < clientBitrateGraphData.current.length; i += 1) {
                      if (clientBitrateGraphData.current[i].name === 'clientBitrate') {
                        f = 1;
                        clientBitrateGraphData.current[i].data.push([currDate, clientBitrate]);
                      }
                    }
                    if (f === 0) {
                      clientBitrateGraphData.current.push({
                        name: 'clientBitrate',
                        data: [[currDate, clientBitrate]],
                      });
                    }
                  }
                }
                prevDate.current = currDate;
                prevBytesReceived.current = currBytesReceived;
                ApexCharts.exec('bitrateGraph', 'updateOptions', {
                  series: clientBitrateGraphData.current,
                });
              }
            }

            /**
             * fir pli and nack count
             */
            if (Object.prototype.hasOwnProperty.call(report, 'firCount')) {
              let f = 0;
              for (let i = 0; i < firPliNackGraphData.current.length; i += 1) {
                if (firPliNackGraphData.current[i].name === 'firCount') {
                  f = 1;
                  firPliNackGraphData.current[i].data.push([currDate, Number(report.firCount)]);
                }
              }
              if (f === 0) {
                firPliNackGraphData.current.push({
                  name: 'firCount',
                  data: [[currDate, Number(report.firCount)]],
                });
              }
            }
            if (Object.prototype.hasOwnProperty.call(report, 'pliCount')) {
              let f = 0;
              for (let i = 0; i < firPliNackGraphData.current.length; i += 1) {
                if (firPliNackGraphData.current[i].name === 'pliCount') {
                  f = 1;
                  firPliNackGraphData.current[i].data.push([currDate, Number(report.pliCount)]);
                }
              }
              if (f === 0) {
                firPliNackGraphData.current.push({
                  name: 'pliCount',
                  data: [[currDate, Number(report.pliCount)]],
                });
              }
            }
            if (Object.prototype.hasOwnProperty.call(report, 'nackCount')) {
              let f = 0;
              for (let i = 0; i < firPliNackGraphData.current.length; i += 1) {
                if (firPliNackGraphData.current[i].name === 'nackCount') {
                  f = 1;
                  firPliNackGraphData.current[i].data.push([currDate, Number(report.nackCount)]);
                }
              }
              if (f === 0) {
                firPliNackGraphData.current.push({
                  name: 'nackCount',
                  data: [[currDate, Number(report.nackCount)]],
                });
              }
              ApexCharts.exec('firPliNackInfo', 'updateOptions', {
                series: firPliNackGraphData.current,
              });
            }

            if (Object.prototype.hasOwnProperty.call(report, 'framesPerSecond')) {
              let f = 0;
              for (let i = 0; i < fpsGraphData.current.length; i += 1) {
                if (fpsGraphData.current[i].name === 'clientFps') {
                  f = 1;
                  fpsGraphData.current[i].data.push([currDate, Number(report.framesPerSecond)]);
                }
              }
              if (f === 0) {
                fpsGraphData.current.push({
                  name: 'clientFps',
                  data: [[currDate, Number(report.framesPerSecond)]],
                });
              }
              ApexCharts.exec('fpsGraphInfo', 'updateOptions', {
                series: fpsGraphData.current,
              });
            }

            if (Object.prototype.hasOwnProperty.call(report, 'frameHeight')) {
              let f = 0;
              for (let i = 0; i < frameSizeGraphData.current.length; i += 1) {
                if (frameSizeGraphData.current[i].name === 'frameSize') {
                  f = 1;
                  frameSizeGraphData.current[i].data.push([currDate, Number(report.frameHeight)]);
                }
              }
              if (f === 0) {
                frameSizeGraphData.current.push({
                  name: 'frameSize',
                  data: [[currDate, Number(report.frameHeight)]],
                });
              }
              ApexCharts.exec('frameSizeInfo', 'updateOptions', {
                series: frameSizeGraphData.current,
              });
            }
          });
        });
      }
    };
    if (open) {
      fetchData();
      infoWebRtcStatsInterval.current = setInterval(() => {
        fetchData();
      }, 5000);
    }
    return () => {
      cleanup();
      clearInterval(infoWebRtcStatsInterval.current);
    };
  }, [open, peerConnection]);

  const cleanup = () => {
    console.log('Graph cleanup');
    clientBitrateGraphData.current = [];
    firPliNackGraphData.current = [];
    fpsGraphData.current = [];
    frameSizeGraphData.current = [];
    ApexCharts.exec('bitrateGraph', 'updateOptions', {
      series: clientBitrateGraphData.current,
    });
    ApexCharts.exec('firPliNackInfo', 'updateOptions', {
      series: firPliNackGraphData.current,
    });
    ApexCharts.exec('frameSizeInfo', 'updateOptions', {
      series: frameSizeGraphData.current,
    });
    ApexCharts.exec('fpsGraphInfo', 'updateOptions', {
      series: fpsGraphData.current,
    });
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} ModalProps={{
      BackdropProps: {
        style: {
          backgroundColor: 'transparent',
          opacity: 0,
        },
      },
    }}
    >
      <Box>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Property</TableCell>
                <TableCell>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {info.map((row) => (
                <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell component="th" scope="row">
                    {row.value}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <Box>
        <Divider>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Stats
          </Typography>
        </Divider>
      </Box>
      <Box sx={{ m: 3 }} dir="ltr">
        <ReactApexChart type="area" height={250} series={[]} options={CLIENT_BITRATE_OPTIONS} />
      </Box>
      <Box sx={{ m: 3 }} dir="ltr">
        <ReactApexChart type="line" height={250} series={[]} options={FPS_OPTIONS} />
      </Box>
      <Box sx={{ m: 3 }} dir="ltr">
        <ReactApexChart type="line" height={250} series={[]} options={FIR_PLI_NACK_OPTIONS} />
      </Box>
      <Box sx={{ m: 3 }} dir="ltr">
        <ReactApexChart type="line" height={250} series={[]} options={FRAME_SIZE_OPTIONS} />
      </Box>
    </Drawer>
  );
}
